<template>
  <div id="page-assemble content-section">


    <div class="clearfix"></div>

    <div class="finder-sections" id="slotFinderWidget">

      <div class="text-left order-confirm">
        <FormKit
            type="form"
            :actions="false"
            form-class="assemble-form"
            @submit="preventSubmission"
        >
          <div class="clearfix"></div>
          <div class="row mt-3">
            <div class="col-md-10" >
              <div>
                {{ scanStage.message }}
                <barcode-scanner
                    :class="!scanStage.scanError ? 'success' : 'error'"
                    barcodeId="qrcode"
                    barcodeAction="find rail slot"
                    v-model="scannedCode"
                    :key="forceBarcodeRender"
                >
                </barcode-scanner>
              </div>
            </div>

            <div class="col-md-2" v-if="showClearButton">
              <FormKit
                  type="button"
                  label="Clear Slot"
                  input-class="$reset btn btn-primary btn-sm"
                  @click="clearSlot"
              />
            </div>
          </div>
        </FormKit>
      </div>

      <div class="confirmation"
           :class="scanStage.highlightColour"
           v-if="scanStage.responseMessage">{{ scanStage.responseMessage }}
      </div>

      <div  class="col-md-2">
        Go back to <a :href="`${mposUrl}/index.php?page=process&mode=pieceup-hanging`">piecing up</a>
      </div>

      <div class="clearfix"></div>
    </div>
  </div>


</template>

<script setup>

import {onMounted, onBeforeUnmount, ref, watch, inject} from 'vue'
import 'bootstrap/dist/css/bootstrap.min.css';
import BarcodeScanner from "@/components/elements/BarcodeScanner.vue";
import BarcodeService from "@/services/BarcodeService";
import PiecingUpService from "@/services/PiecingUpService";
import barcodeService from "@/services/BarcodeService";
import { setSidebarTitle, resetSidebarTitle } from '@/components/sidebar/state'

onMounted(() => {
  setSidebarTitle('Slot Locator') // custom title for this view
})

onBeforeUnmount(() => {
  resetSidebarTitle() // back to MPOS
})

const SCAN_STAGE_TICKET = 1;
const SCAN_STAGE_SLOT = 2;
const mposUrl = ref(process.env.VUE_APP_LEGACY_MPOS_URL);

let scanStage = ref({
  stage: 1,
  message: "Scan the ticket barcode to find the rail slot",
  responseMessage: "",
  currentServiceTicket: {},
  currentRailSlot: {},
  scanError: false,
  isComplete: false,
  highlightColour: "",
});

const scannedCode = ref("");
const forceBarcodeRender = ref(0);
const showClearButton = ref(false);

const preventSubmission = () => {
  return false;
};

/**
 * We watch the barcode input field as characters are coming in like from a keyboard one by one.
 * After each input change we check if the barcode is complete by detecting the expected type.
 * However, as we don't know when the barcode is complete we wait for 100 ms for another character
 * before we analyse the value. This should be fast enough to not see the delay and slow enough
 * to get all characters until the barcode is complete.
 */
let scannerTimeoutId = undefined;
watch(scannedCode, async (value) => {
  if (typeof scannerTimeoutId === "number") {
    clearTimeout(scannerTimeoutId);
  }
  if (value.length > 0) {
    // Wait 100 ms before throwing an error message but only if the timer does not get reset before
    scannerTimeoutId = setTimeout(async () => {
      await recordBarcode(value);
    }, 100);
  }
});

/**
 * Record the barcode scanned and react accordingly
 *
 * We have one input field used for barcode scanning. The type of barcode we scan depends on the stage we are in.
 * We have two stages: ticket and slot and react accordingly.
 *
 * @param value
 * @returns {Promise<void>}
 */
const recordBarcode = async (value) => {
  showClearButton.value = false;
  // We only react if we actually have a value
  if (value !== "") {
    // We get the details of the barcode to find out what type of barcode it is
    let barcodeDetails = BarcodeService.getBarcodeDetails(value);

    // Log the barcode scanned, no matter what stage we are in, but log the stage
    if (scanStage.value.stage === SCAN_STAGE_TICKET) {
      BarcodeService.logBarcode(barcodeDetails.barcode, "slot locator ticket scan");
    } else if (scanStage.value.stage === SCAN_STAGE_SLOT) {
      BarcodeService.logBarcode(barcodeDetails.barcode, "slot locator slot scan");
    } else {
      BarcodeService.logBarcode(barcodeDetails.barcode, "unexpected slot locator scan");
    }

    if (scanStage.value.stage === SCAN_STAGE_TICKET && barcodeDetails.type !== "ticket") {
      scanStage.value.scanError = true;
      scanStage.value.responseMessage = "Invalid barcode type scanned. Scan the service ticket barcode";
      scanStage.value.highlightColour = "error";
    }
    else if (scanStage.value.stage === SCAN_STAGE_TICKET) {
      PiecingUpService.getSlotForTicket(barcodeDetails.id).then((response) => {
        if (response.status === "OK") {
          scanStage.value.responseMessage = "Replace ticket for slot: " + response.slot.name;
          scanStage.value.message = "Now scan the slot barcode to confirm the ticket change";
          scanStage.value.stage = SCAN_STAGE_SLOT;
          scanStage.value.scanError = false;
          scanStage.value.highlightColour = "";
          scanStage.value.currentRailSlot = response.slot;
          scanStage.value.currentServiceTicket = response.serviceTicket;
          console.log("Scan stage", scanStage.value);
        } else {
          scanStage.value.responseMessage = response.message;
          scanStage.value.currentRailSlot = {};
          scanStage.value.highlightColour = "error";
        }
      });
    }
    else if (scanStage.value.stage === SCAN_STAGE_SLOT && barcodeDetails.type !== "pu-rail") {
      scanStage.value.scanError = true;
      scanStage.value.responseMessage = "Invalid barcode type scanned. Scan the rail slot barcode "
        + scanStage.value.currentRailSlot.name;
      scanStage.value.highlightColour = "error";
    }
    else if (scanStage.value.stage === SCAN_STAGE_SLOT) {
      const slotBarcode = "PUR" + barcodeDetails.id;
      PiecingUpService.confirmSlotForTicket(slotBarcode, scanStage.value.currentServiceTicket.serviceticket_id)
          .then((response) => {
            scanStage.value.isComplete = response.isComplete;
            if (response.status === "OK" && response.isValidServiceTicket && response.isComplete) {

              resetScanStage();
              scanStage.value.responseMessage = "All garments for ticket pieced up";
              scanStage.value.highlightColour = "success";
              showClearButton.value = true;
              scanStage.value.scanError = false;
              scanStage.value.currentRailSlot = response.slot;
            } else if (response.status === "OK" && response.isValidServiceTicket) {
              resetScanStage();
              showClearButton.value = false;
              scanStage.value.scanError = false;
              scanStage.value.highlightColour = "success";
            } else if (response.status === "OK" && !response.isValidServiceTicket) {
              showClearButton.value = false;
              scanStage.value.scanError = true;
              scanStage.value.highlightColour = "error";
              scanStage.value.responseMessage = "Incorrect rail scanned! Scan " + scanStage.value.currentRailSlot.name;
            } else {
              scanStage.value.responseMessage = response.message;
              showClearButton.value = false;
              scanStage.value.highlightColour = "error";
            }
      });
    } else {
      scanStage.value.scanError = true;
      scanStage.value.responseMessage = "Invalid barcode scanned";
    }
  }
  scannedCode.value = "";
  forceBarcodeRender.value++;
};

const clearSlot = () => {
  if( !confirm("This will clear the slot. You will need to take the items to packing. Continue?") ) {
    return false;
  }

  PiecingUpService.clearSlot(scanStage.value.currentRailSlot.barcode).then((response) => {
    console.log(response);
  });

  resetScanStage();
  scanStage.value.responseMessage = "Slot cleared";
  showClearButton.value = false;
};

const resetScanStage = () => {
  scanStage.value.stage = SCAN_STAGE_TICKET;
  scanStage.value.message = "Scan the ticket barcode to find the rail slot";
  scanStage.value.highlightColour = "";
  scanStage.value.responseMessage = "Ticket replacement complete";
  scanStage.value.currentServiceTicket = {};
  scanStage.value.currentRailSlot = {};
  scanStage.value.isComplete = false;
};

</script>

<style scoped>

.disabled {
  color: var(--disabled-color)!important;
}
.hidden {
  display: none;
}
.full-width {
  width: 100%;
}

.finder-sections {
  width: 90%;
  margin: 30px auto;
  padding: 20px;
  border: 1px solid var(--form-border-color);
  box-shadow: 0 2px 3px var(--form-shadow-color);
  display: grid;
}

small {
  margin-bottom: 10px;
  padding-top: 5px;
  display: block;
}
.formkit-input.barcode-input{
  width: 80%;
  display: inline-block;
  background-color: transparent;
}
.epos-barcode-scanner-status {
  font-weight: bold;
}
.barcode-inner {
  border-radius: 10px;
  display: block;
  font-size: 12px;
}

.barcode-input {
  background-color: #fff;
}
.barcode-input.full-width {
  width: 100%;
}

.confirmation {
  background-color: darkblue;
  color: white;
}

.barcode-input.success,
.confirmation.success {
  background-color: var( --light-green-color);
  color: black;
}
.barcode-input.error,
.confirmation.error {
  background-color: var( --light-red-color);
  color: black;
}
.input {
  margin: 10px auto;
}
.customer-state {
  background-color: var( --success-color)!important;
}
.customer-state.handling {
  background-color: var(--handling-color)!important;
}

.input label {
  display: block;
  color: #4e4e4e;
  margin-bottom: 6px;
}

.input input {
  font: inherit;
  width: 100%;
  padding: 6px 12px;
  border-radius: 7px;
  box-sizing: border-box;
  border: 1px solid #ccc;
}

.input input:focus {
  outline: none;
  border: 1px solid #521751;
  background-color: #eee;
}

.submit button {
  border: 1px solid #521751;
  color: #521751;
  padding: 10px 20px;
  font: inherit;
  cursor: pointer;
  border-radius: 18px;
}

.submit button:hover,
.submit button:active {
  background-color: #521751;
  color: white;
}

.submit button[disabled],
.submit button[disabled]:hover,
.submit button[disabled]:active {
  border: 1px solid #ccc;
  background-color: transparent;
  color: #ccc;
  cursor: not-allowed;
}

.pieceUpMessage {
  margin-top: 20px;
  color: green;
  font-weight: bold;
  font-size: 24px;
}
</style>